const state = {
  programs: [],
  activePrograms: [],
  currentProgram: null,
  loading: false,
  error: null,
  activeProgramsCache: null,
  activeProgramsCacheTime: null
}

const mutations = {
  SET_PROGRAMS (state, programs) {
    state.programs = programs
  },
  SET_ACTIVE_PROGRAMS (state, { programs, timestamp }) {
    state.activePrograms = programs
    state.activeProgramsCache = programs
    state.activeProgramsCacheTime = timestamp
  },
  SET_CURRENT_PROGRAM (state, program) {
    state.currentProgram = program
  },
  ADD_PROGRAM (state, program) {
    state.programs.push(program)
    if (program.is_active) {
      state.activePrograms.push(program)
    }
  },
  UPDATE_PROGRAM (state, program) {
    const index = state.programs.findIndex(p => p.id === program.id)
    if (index !== -1) {
      state.programs.splice(index, 1, program)
    }

    const activeIndex = state.activePrograms.findIndex(p => p.id === program.id)
    if (program.is_active) {
      if (activeIndex === -1) {
        state.activePrograms.push(program)
      } else {
        state.activePrograms.splice(activeIndex, 1, program)
      }
    } else {
      if (activeIndex !== -1) {
        state.activePrograms.splice(activeIndex, 1)
      }
    }

    if (state.currentProgram && state.currentProgram.id === program.id) {
      state.currentProgram = program
    }
  },
  REMOVE_PROGRAM (state, programId) {
    state.programs = state.programs.filter(p => p.id !== programId)
    state.activePrograms = state.activePrograms.filter(p => p.id !== programId)
    if (state.currentProgram && state.currentProgram.id === programId) {
      state.currentProgram = null
    }
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ERROR (state, error) {
    state.error = error
  }
}

const actions = {
  async fetchPrograms ({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const programs = await this._vm.$loyaltyService.getPrograms()
      commit('SET_PROGRAMS', programs)
      return programs
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchActivePrograms ({ commit, state }) {
    // Check cache (5 minutes)
    const cacheTimeout = 5 * 60 * 1000 // 5 minutes
    if (state.activeProgramsCache && state.activeProgramsCacheTime) {
      const cacheAge = Date.now() - state.activeProgramsCacheTime
      if (cacheAge < cacheTimeout) {
        return state.activeProgramsCache
      }
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const programs = await this._vm.$loyaltyService.getActivePrograms()
      commit('SET_ACTIVE_PROGRAMS', { programs, timestamp: Date.now() })
      return programs
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchProgram ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const program = await this._vm.$loyaltyService.getProgram(id)
      commit('SET_CURRENT_PROGRAM', program)
      return program
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createProgram ({ commit }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const program = await this._vm.$loyaltyService.createProgram(data)
      commit('ADD_PROGRAM', program)
      return program
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateProgram ({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const program = await this._vm.$loyaltyService.updateProgram(id, data)
      commit('UPDATE_PROGRAM', program)
      return program
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteProgram ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await this._vm.$loyaltyService.deleteProgram(id)
      commit('REMOVE_PROGRAM', id)
      return true
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  programs: state => state.programs,
  activePrograms: state => state.activePrograms,
  currentProgram: state => state.currentProgram,
  loading: state => state.loading,
  error: state => state.error,
  programById: state => id => {
    return state.programs.find(p => p.id === id) || null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
